import { ApplicationRef, createComponent, EnvironmentInjector, inject, Injectable } from '@angular/core';
import { where } from '@firebase/firestore';
import { TranslateService } from '@ngx-translate/core';
import { IService } from '@scandium-oy/ngx-scandium';
import { format } from 'date-fns';
import { Observable } from 'rxjs';
import { SiteReport } from '../models/site-report.model';
import { css } from '../sites/site/components/reports/new/report-style';
import { SiteReportPrintComponent, SiteReportPrintPayload } from '../sites/site/components/reports/print/site-report-print.component';
import { convertTimestamp } from '../utility/time';
import { FirestoreService } from './firestore.service';
import { PrintService } from './print.service';

const itemCollection = 'site-reports';

@Injectable({
  providedIn: 'root',
})
export class SiteReportsService implements IService<SiteReport> {
  private appRef = inject(ApplicationRef);
  private environmentInjector = inject(EnvironmentInjector);
  private firestore = inject(FirestoreService);
  private printService = inject(PrintService);
  private translate = inject(TranslateService);

  async save(item: SiteReport) {
    return this.firestore.save<SiteReport>(itemCollection, item);
  }

  async update(item: SiteReport) {
    return this.firestore.update(itemCollection, item);
  }

  async updateOnly(guid: string, item: object) {
    return this.firestore.updateOnly(itemCollection, guid, item);
  }

  async remove(item: SiteReport) {
    return this.firestore.delete(itemCollection, item);
  }

  get(guid: string): Observable<SiteReport> {
    return this.firestore.get<SiteReport>(itemCollection, guid);
  }

  getList(options?: { site?: string; contractor?: string; creator?: string; name?: string }): Observable<SiteReport[]> {
    const queryConstraints = [];
    if (options?.site) {
      queryConstraints.push(where('site', '==', options.site));
    }
    if (options?.contractor) {
      queryConstraints.push(where('contractor.guid', '==', options.contractor));
    }
    if (options?.creator) {
      queryConstraints.push(where('creator.guid', '==', options.creator));
    }
    if (options?.name) {
      queryConstraints.push(where('name', '==', options.name));
    }

    return this.firestore
      .getList<SiteReport>(itemCollection, undefined, queryConstraints);
  }

  async print(
    payload: Omit<SiteReportPrintPayload, 'labels'>,
    options?: { output?: boolean; title?: string; dateLabel?: string; brandName?: string; logoUrl?: string | null },
  ) {
    const host = document.createElement('div');
    document.body.appendChild(host);

    const componentRef = createComponent(SiteReportPrintComponent, {
      environmentInjector: this.environmentInjector,
      hostElement: host,
    });
    this.appRef.attachView(componentRef.hostView);
    componentRef.setInput('payload', {
      ...payload,
      labels: {
        site: this.translate.instant('timesheet.site') as string,
        users: this.translate.instant('users.title') as string,
        tasks: this.translate.instant('kanban.title') as string,
        created: this.translate.instant('kanban.status.created') as string,
        inProgress: this.translate.instant('kanban.status.inProgress') as string,
        done: this.translate.instant('kanban.status.done') as string,
        room: this.translate.instant('calendar.site.area') as string,
        assignees: this.translate.instant('site.responsiblePersons') as string,
        deadline: this.translate.instant('kanban.dateDeadline') as string,
      },
    });
    componentRef.changeDetectorRef.detectChanges();

    const element = host.firstElementChild as HTMLElement ?? host;
    const createdDate = this.toDate(payload.createdAt) ?? new Date();
    const fileName = `tehtäväraportti_${payload.site?.name ?? 'site'}_${payload.creatorName ?? 'report'}_${createdDate.toISOString()}`;
    const title = options?.title ?? this.translate.instant(`report.new.list.${payload.reportName}`) as string;
    const dateLabel = options?.dateLabel ?? format(createdDate, 'd.M.yyyy');

    try {
      const result = await this.printService.printA4Document(fileName, element, {
        css,
        output: options?.output ?? false,
        header: {
          title,
          dateLabel,
          brandName: options?.brandName,
          logoUrl: options?.logoUrl,
        },
      });
      return result;
    } finally {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      host.remove();
    }
  }

  private toDate(value: Date | { toDate: () => Date } | string | number | null | undefined) {
    const date = convertTimestamp(value);
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
}
